import { uuidModule } from "@/modules/uuid";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { OptimisticSyncUpdate, SyncModelData, SyncModelKind } from "@/store/sync/types";
import { type UploadedFileModelData, type MimeType } from "@/store/uploaded-files/types";
import { type IBatchCreateUploadedFileOperation } from "@/store/sync/operations/types";

export class BatchCreateUploadedFileOperation extends BaseSyncOperation<IBatchCreateUploadedFileOperation> {
  get operationKind() {
    return "BATCH_CREATE_UPLOADED_FILE" as const;
  }

  async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const optimisticUpdates: OptimisticSyncUpdate<SyncModelData>[] = [];

    for (let i = 0; i < this.payload.uploaded_files.length; i++) {
      const fileUpdate = this.generateUploadedFileUpdate(i);
      if (fileUpdate) {
        optimisticUpdates.push(fileUpdate);
      }
    }

    return optimisticUpdates;
  }

  protected generateUploadedFileUpdate(index: number): OptimisticSyncUpdate<UploadedFileModelData> {
    const filePayload = this.payload.uploaded_files[index];
    return {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: filePayload.id,
        model_kind: SyncModelKind["UploadedFile"],
        model_version: 1,
        model_data: {
          id: filePayload.id,
          mime_type: (filePayload.mime_type || "unknown") as MimeType,
          uploaded_file_batch_id: this.payload.uploaded_file_batch_id,
          normalized_filename: filePayload.normalized_filename,
          file_size_bytes: filePayload.file_size_bytes,
          original_filename: filePayload.original_filename,
          original_filepath: filePayload.original_filepath,
          modified_at_timestamp: filePayload.modified_at_timestamp ?? null,
        },
        model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
      },
    };
  }

  async triggerRecompute(): Promise<void> {
    for (const filePayload of this.payload.uploaded_files) {
      await this.store.uploadedFiles.recompute(filePayload.id);
    }
  }
}
